import React from 'react';
import { TreeStructure, ArrowsOutSimple, ArrowsInSimple } from '@phosphor-icons/react';
import { Button, Text, LoadingOrbit, Flex } from '@chatsdet/ui';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useSiteGraph } from '../hooks/useSiteGraph';
import { useSetSlugOverride, useRemoveSlugOverride } from '../hooks/useSlugOverrides';
import { useSetQaExclusion } from '../hooks/useQaExclusion';
import { buildPathTree } from '../types';
import type { PathTreeNode } from '../types';
import { SiteGraphTreeNode } from './SiteGraphTreeNode';

type SiteGraphTreeProps = {
  propertyId: string;
};

function collectAllPaths(nodes: PathTreeNode[]): Set<string> {
  const paths = new Set<string>();
  function walk(tn: PathTreeNode) {
    if (tn.children.length > 0) {
      paths.add(tn.fullPath);
      tn.children.forEach(walk);
    }
  }
  nodes.forEach(walk);
  return paths;
}

export function SiteGraphTree({ propertyId }: SiteGraphTreeProps) {
  const { data, isLoading, error } = useSiteGraph(propertyId);
  const { user } = useAuth();
  const canManageQaExclusions = !!user?.features?.qaRuns;
  const navigate = useNavigate();
  const setSlugOverride = useSetSlugOverride(propertyId);
  const removeSlugOverride = useRemoveSlugOverride(propertyId);
  const setQaExclusion = useSetQaExclusion(propertyId);

  const tree = React.useMemo(() => {
    if (!data) return [];
    return buildPathTree(data.nodes, data.slugPatterns);
  }, [data]);

  const [expandedPaths, setExpandedPaths] = React.useState<Set<string>>(() => new Set<string>());

  // Auto-expand first two levels on data load
  React.useEffect(() => {
    if (tree.length > 0) {
      const initial = new Set<string>();
      for (const root of tree) {
        initial.add(root.fullPath);
        for (const child of root.children) {
          initial.add(child.fullPath);
        }
      }
      setExpandedPaths(initial);
    }
  }, [tree]);

  const handleToggle = React.useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleExpandAll = () => setExpandedPaths(collectAllPaths(tree));
  const handleCollapseAll = () => setExpandedPaths(new Set());

  const handleNavigate = (canonicalPageId: string, propId: string) => {
    navigate({
      to: '/pages/$propertyId/canonical/$canonicalPageId',
      params: { propertyId: propId, canonicalPageId },
    });
  };

  const handleMarkSlug = React.useCallback(
    (parentPath: string, mode: 'slug' | 'not-slug') => {
      setSlugOverride.mutate({ parentPath, mode });
    },
    [setSlugOverride]
  );

  const handleRemoveOverride = React.useCallback(
    (parentPath: string) => {
      removeSlugOverride.mutate({ parentPath });
    },
    [removeSlugOverride]
  );

  const handleToggleExclude = React.useCallback(
    (canonicalPageIds: string[], excluded: boolean) => {
      setQaExclusion.mutate({ canonicalPageIds, excluded });
    },
    [setQaExclusion]
  );

  const allPaths = React.useMemo(() => collectAllPaths(tree), [tree]);
  const isAllExpanded = allPaths.size > 0 && allPaths.size === expandedPaths.size && [...allPaths].every((p) => expandedPaths.has(p));

  if (isLoading) {
    return (
      <Flex align="center" justify="center" className="h-64">
        <LoadingOrbit />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex align="center" justify="center" className="h-64">
        <Text className="text-destructive">Failed to load site graph</Text>
      </Flex>
    );
  }

  if (tree.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-sm px-6">
          <TreeStructure size={48} className="mx-auto mb-4 text-muted-foreground/40" />
          <Text size="lg" weight="medium" className="mb-2">
            No pages discovered yet
          </Text>
          <Text size="sm" className="text-muted-foreground">
            Pages will appear here once a crawl or test run discovers them.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="flex items-center justify-end px-3 py-2 border-b bg-muted/30">
        <Button
          variant="ghost"
          size="sm"
          onPress={isAllExpanded ? handleCollapseAll : handleExpandAll}
          className="w-7 p-0"
          aria-label={isAllExpanded ? 'Collapse all' : 'Expand all'}
        >
          {isAllExpanded ? <ArrowsInSimple size={14} /> : <ArrowsOutSimple size={14} />}
        </Button>
      </div>

      {/* Tree body */}
      <div className="overflow-auto max-h-[calc(100vh-280px)] py-1">
        {tree.map((rootNode) => (
          <SiteGraphTreeNode
            key={rootNode.fullPath}
            treeNode={rootNode}
            depth={0}
            expandedPaths={expandedPaths}
            onToggle={handleToggle}
            onNavigate={handleNavigate}
            onMarkSlug={handleMarkSlug}
            onRemoveOverride={handleRemoveOverride}
            onToggleExclude={canManageQaExclusions ? handleToggleExclude : undefined}
            propertyId={propertyId}
          />
        ))}
      </div>
    </div>
  );
}
